import { Box, Flex } from '@chakra-ui/react';
import { CheckCircle2, Lock } from 'lucide-react';
import {
  HStack,
  Text,
} from '@ayble-health/dashboard-components';
import { StatusPill } from './StatusPill';
import type { ProviderVisit, VisitStatus } from '../../models/providerHome';
import { useUpdateVisit } from '../../api/providerHome/mutations';
import {
  ACTIVE_BAR,
  ACTIVE_BG,
  ACTIVE_BORDER,
  CARD_BORDER,
  INK,
  PLUM,
  SUBTLE,
  SUBTLE_STRONG,
  TEAL,
} from './tokens';

const fmtTime = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

type Props = {
  visit: ProviderVisit;
  /** The fixture's represented "now" — the isNext visit's time. Null on the Tomorrow tab (nothing is past). */
  mockNow: Date | null;
};

export const VisitRow = ({ visit, mockNow }: Props) => {
  const { mutate } = useUpdateVisit();

  const confirm = () => mutate({ id: visit.id, patch: { status: 'confirmed' } });

  const isPast = mockNow != null && new Date(visit.time) < mockNow && !visit.isNext;

  const displayStatus: VisitStatus = isPast
    ? visit.status === 'confirmed'
      ? 'occurred'
      : visit.status === 'unconfirmed'
      ? 'no_show'
      : visit.status === 'late_cancel'
      ? 'cancelled'
      : visit.status
    : visit.status;

  // Rescheduled and late_cancel use plum (DESIGN.md appointment table); everything else uses teal.
  const isRed = visit.status === 'rescheduled' || visit.status === 'late_cancel';
  const borderLeftColor = visit.isNext ? ACTIVE_BAR : isRed ? PLUM : TEAL;

  // Teal time pill only for upcoming, non-rose rows.
  const showTimePill = !isPast && !isRed;

  const renderNoteState = () => {
    if (!isPast || visit.status !== 'confirmed') return null;
    if (visit.noteState === 'signed') {
      return (
        <HStack gap={1} align="center" flexShrink={0}>
          <CheckCircle2 size={12} color={SUBTLE} />
          <Text fontSize="xs" color={SUBTLE} whiteSpace="nowrap">
            Note Signed
          </Text>
        </HStack>
      );
    }
    if (visit.noteState === 'lock') {
      return (
        <HStack gap={1} align="center" flexShrink={0}>
          <Lock size={12} color={SUBTLE} />
          <Text fontSize="xs" color={SUBTLE} whiteSpace="nowrap">
            Note Locked
          </Text>
        </HStack>
      );
    }
    return null;
  };

  return (
    <Flex
      align="center"
      gap={3}
      h="48px"
      px={4}
      bg={visit.isNext ? ACTIVE_BG : 'white'}
      borderWidth="1px"
      borderColor={visit.isNext ? ACTIVE_BORDER : CARD_BORDER}
      borderLeftWidth="3px"
      borderLeftColor={borderLeftColor}
      borderRadius="12px"
      boxShadow="0 1px 2px rgba(16,24,40,0.04)"
      opacity={isPast ? 0.65 : 1}
      overflow="hidden"
      cursor="pointer"
      onClick={() => window.open(`/provider-home/patient/${visit.patientId}`, '_self')}
      _hover={{ filter: 'brightness(0.96)' }}
      transition="filter 0.12s ease"
    >
      {showTimePill ? (
        <Box flexShrink={0} bg={ACTIVE_BG} borderRadius="6px" px={2} py="3px">
          <Text fontSize="xs" fontWeight={600} color="seafoam" whiteSpace="nowrap">
            {fmtTime(visit.time)}
          </Text>
        </Box>
      ) : (
        <Text
          w="72px"
          flexShrink={0}
          fontSize="xs"
          fontWeight={500}
          color={SUBTLE_STRONG}
          whiteSpace="nowrap"
        >
          {fmtTime(visit.time)}
        </Text>
      )}

      <HStack gap={2} flex={1} minW={0} align="baseline">
        <Text fontWeight={600} fontSize="sm" color={INK} whiteSpace="nowrap">
          {visit.patientName}
        </Text>
        <Text
          color={SUBTLE}
          fontSize="xs"
          whiteSpace="nowrap"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {visit.visitType}
        </Text>
      </HStack>

      <HStack gap={2} flexShrink={0}>
        {renderNoteState()}
        <StatusPill
          status={displayStatus}
          rescheduledTo={visit.rescheduledTo}
          onConfirm={!isPast && visit.status === 'unconfirmed' ? confirm : undefined}
        />
      </HStack>
    </Flex>
  );
};
